import api from '@/lib/api';


interface RegisterData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: 'ADMIN' | 'MANAGER' | 'STAFF';
  };
  // Tokens are now in HttpOnly cookies, not response body
}

interface RefreshResponse {
  refreshed: boolean;
}

interface CSRFResponse {
  csrfToken: string;
  expires: string;
}

// CSRF token management
let cachedCSRFToken: string | null = null;
let csrfTokenExpiry: Date | null = null;
let csrfTokenPromise: Promise<string> | null = null; // Track ongoing requests

const getCSRFToken = async (forceRefresh: boolean = false): Promise<string> => {
  // Clear cache if force refresh is requested
  if (forceRefresh) {
    cachedCSRFToken = null;
    csrfTokenExpiry = null;
    csrfTokenPromise = null;
  }
  
  // Return cached token if still valid (but not for single-use operations)
  if (!forceRefresh && cachedCSRFToken && csrfTokenExpiry && new Date() < csrfTokenExpiry) {
    // For non-critical operations, we can reuse the token
    // But for critical operations (login, register, etc), we should get a fresh token
    return cachedCSRFToken;
  }

  // If there's an ongoing request, wait for it instead of creating a new one
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }

  // Create new request and cache the promise
  csrfTokenPromise = (async () => {
    try {
      const response = await api.get<{ success: boolean; data: CSRFResponse }>('/auth/csrf-token');
      const { csrfToken, expires } = response.data.data;
      
      cachedCSRFToken = csrfToken;
      csrfTokenExpiry = new Date(expires);
      csrfTokenPromise = null; // Clear the promise after success
      
      return csrfToken;
    } catch (error) {
      csrfTokenPromise = null; // Clear the promise after failure
      console.error('Failed to get CSRF token:', error); // TEMPORARILY ENABLED FOR DEBUGGING
      throw new Error('Unable to get security token. Please try again.');
    }
  })();

  return csrfTokenPromise;
};

// Clear cached CSRF token
const clearCSRFToken = () => {
  cachedCSRFToken = null;
  csrfTokenExpiry = null;
  csrfTokenPromise = null;
};

export const authService = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('Starting login process...'); // TEMPORARILY ENABLED FOR DEBUGGING
      // Get fresh CSRF token for login (critical operation)
      const csrfToken = await getCSRFToken(true);
      console.log('Got CSRF token:', csrfToken.substring(0, 10) + '...'); // TEMPORARILY ENABLED FOR DEBUGGING
      const response = await api.post('/auth/login', 
        { email, password }, 
        { headers: { 'X-CSRF-Token': csrfToken } }
      );
      console.log('Login successful:', response.data); // TEMPORARILY ENABLED FOR DEBUGGING
      // Don't clear token immediately - let it expire naturally
      return response.data;
    } catch (error) {
      console.error('Login error:', error); // TEMPORARILY ENABLED FOR DEBUGGING
      throw error;
    }
  },

  register: async (data: RegisterData): Promise<AuthResponse> => {
    // console.log('Sending registration request to:', api.defaults.baseURL + '/auth/register'); // DISABLED TO STOP SPAM
    // console.log('Registration data being sent:', { ...data, password: '[REDACTED]' }); // DISABLED TO STOP SPAM
    
    try {
      // Get fresh CSRF token for registration (critical operation)
      const csrfToken = await getCSRFToken(true);
      const response = await api.post('/auth/register', data, {
        headers: { 'X-CSRF-Token': csrfToken }
      });
      // console.log('Raw API response:', response); // DISABLED TO STOP SPAM
      // console.log('Response data:', response.data); // DISABLED TO STOP SPAM
      // Don't clear token immediately - let it expire naturally
      return response.data;
    } catch (error: any) {
      // console.error('API registration error:', error); // DISABLED TO STOP SPAM
      // console.error('API error response:', error.response); // DISABLED TO STOP SPAM
      
      // Better error handling for network issues
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') {
        throw new Error('Unable to connect to server. Please check if the backend service is running.');
      }
      
      throw error;
    }
  },

  refresh: async (): Promise<RefreshResponse> => {
    // Refresh token is now in HttpOnly cookie, no need to pass it
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  logout: async (): Promise<void> => {
    // Get fresh CSRF token for logout (critical operation)
    const csrfToken = await getCSRFToken(true);
    await api.post('/auth/logout', {}, {
      headers: { 'X-CSRF-Token': csrfToken }
    });
    // Clear all tokens on logout
    clearCSRFToken();
  },

  getProfile: async () => {
    const response = await api.get('/auth/me');
    return response;
  },

  updateProfile: async (data: Partial<RegisterData>) => {
    const response = await api.put('/auth/profile', data);
    return response;
  },

  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    // Get fresh CSRF token for password change (critical operation)
    const csrfToken = await getCSRFToken(true);
    await api.post('/auth/change-password', 
      { currentPassword: oldPassword, newPassword }, 
      { headers: { 'X-CSRF-Token': csrfToken } }
    );
    // Don't clear token immediately - let it expire naturally
  },

  // Utility method to get CSRF token for external use
  getCSRFToken,
  
  // Clear authentication state
  clearAuthState: () => {
    clearCSRFToken();
  },

  forgotPassword: async (email: string): Promise<void> => {
    await api.post('/auth/forgot-password', { email });
  },

  resetPassword: async (token: string, password: string): Promise<void> => {
    await api.post('/auth/reset-password', { token, password });
  },
};
